import { Box, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material'

export type BarSegment = {
  percentage: number
  color: string
  value: number
  label: string
}

export type LegendItem = {
  color: string
  value: string
  label: string
  tooltipValue?: string
}

type BasePercentageBarProps = {
  segments: BarSegment[]
  totalLabel?: string
  totalValue?: number
  legends: LegendItem[]
  extraContent?: React.ReactNode
  showTotal?: boolean
  unitLabel: string
}

const BasePercentageBar = ({
  segments,
  totalLabel,
  totalValue,
  legends,
  extraContent,
  showTotal = true,
  unitLabel,
}: BasePercentageBarProps) => {
  const theme = useTheme()
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))

  const hasSegments = segments.some(s => s.percentage > 0)

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 1,
          mt: 2,
          height: '10px',
          width: '100%',
        }}
      >
        {hasSegments ? (
          segments
            .filter(segment => segment.percentage > 0)
            .map((segment, index) => (
              <Box
                key={index}
                sx={{
                  width: `${segment.percentage}%`,
                  height: 10,
                  bgcolor: segment.color,
                  borderRadius: '10px',
                }}
              />
            ))
        ) : (
          <Box
            sx={{
              width: '100%',
              height: 10,
              bgcolor: theme.palette.secondary.light,
              borderRadius: '10px',
            }}
          />
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: isDownSm ? 'center' : 'space-between',
          alignItems: 'center',
          gap: isDownSm ? 0 : 2,
          flexDirection: isDownSm ? 'column' : 'row',
        }}
      >
        {totalLabel && (
          <Typography my={isDownSm ? 1 : 0} fontWeight={500}>
            {totalLabel}
          </Typography>
        )}

        {showTotal && totalValue !== undefined && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1 }}>
            <Typography variant="h3" fontWeight={700} fontSize={25} sx={{ color: 'text.highEmphasis' }}>
              {totalValue}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.base' }}>
              {unitLabel}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2, my: 1 }}>
          {legends.map((legend, index) => (
            <Box key={index} sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="body1" sx={{ color: 'text.base' }}>
                {legend.label}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Box
                  sx={{
                    width: '10px',
                    height: '10px',
                    bgcolor: legend.color,
                    borderRadius: '10px',
                  }}
                />
                <Tooltip title={legend.tooltipValue || ''} arrow placement="top">
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{
                      color: 'text.highEmphasis',
                      cursor: legend.tooltipValue ? 'help' : 'default',
                      textDecoration: legend.tooltipValue ? 'underline dotted' : 'none',
                    }}
                  >
                    {legend.value}
                  </Typography>
                </Tooltip>
              </Box>
            </Box>
          ))}
        </Box>

        <Box sx={{ display: 'flex', gap: 2, m: 1 }}>{extraContent}</Box>
      </Box>
    </>
  )
}

export default BasePercentageBar
